import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';

import { createEditorHistory } from './history';
import { removeColor } from './pixels';
import type { RGB } from './types';

export const STICKER_CANVAS_SIZE = 512;

export type StickerCanvasTool = 'background' | 'erase' | 'restore' | 'text' | 'emoji' | 'move';

export interface StickerCanvasLayer {
  id: string;
  kind: 'text' | 'emoji';
  value: string;
  x: number;
  y: number;
  scale: number;
  color: string;
  fontSize: number;
}

interface TextLayerOptions {
  color?: string;
  fontSize?: number;
}

interface EditorSnapshot {
  imageData: ImageData;
  layers: StickerCanvasLayer[];
}

interface StrokeState {
  imageData: ImageData;
  lastX: number;
  lastY: number;
}

interface MoveState {
  layerId: string;
  offsetX: number;
  offsetY: number;
}

const BRUSH_RADIUS = 16;

const copyImageData = (imageData: ImageData) => new ImageData(
  new Uint8ClampedArray(imageData.data), imageData.width, imageData.height,
);

const copyLayers = (layers: StickerCanvasLayer[]) => layers.map((layer) => ({ ...layer }));

const clampCoordinate = (value: number) => Math.min(STICKER_CANVAS_SIZE - 1, Math.max(0, value));

const drawLayers = (context: CanvasRenderingContext2D, layers: StickerCanvasLayer[]) => {
  layers.forEach((layer) => {
    context.save();
    context.translate(layer.x, layer.y);
    context.scale(layer.scale, layer.scale);
    context.fillStyle = layer.kind === 'text' ? layer.color : '#ffffff';
    context.font = layer.kind === 'emoji' ? '48px sans-serif' : `bold ${layer.fontSize}px sans-serif`;
    context.fillText(layer.value, 0, 0);
    context.restore();
  });
};

const paintCircle = (
  imageData: ImageData,
  source: ImageData,
  x: number,
  y: number,
  restore: boolean,
) => {
  const minX = Math.max(0, Math.floor(x - BRUSH_RADIUS));
  const maxX = Math.min(imageData.width - 1, Math.ceil(x + BRUSH_RADIUS));
  const minY = Math.max(0, Math.floor(y - BRUSH_RADIUS));
  const maxY = Math.min(imageData.height - 1, Math.ceil(y + BRUSH_RADIUS));

  for (let pixelY = minY; pixelY <= maxY; pixelY += 1) {
    for (let pixelX = minX; pixelX <= maxX; pixelX += 1) {
      if ((pixelX - x) ** 2 + (pixelY - y) ** 2 > BRUSH_RADIUS ** 2) continue;
      const index = (pixelY * imageData.width + pixelX) * 4;
      if (restore) {
        imageData.data[index] = source.data[index];
        imageData.data[index + 1] = source.data[index + 1];
        imageData.data[index + 2] = source.data[index + 2];
        imageData.data[index + 3] = source.data[index + 3];
      } else {
        imageData.data[index + 3] = 0;
      }
    }
  }
};

const paintStroke = (
  imageData: ImageData,
  source: ImageData,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  restore: boolean,
) => {
  const distance = Math.hypot(toX - fromX, toY - fromY);
  const steps = Math.max(1, Math.ceil(distance / (BRUSH_RADIUS / 2)));
  for (let step = 0; step <= steps; step += 1) {
    const ratio = step / steps;
    paintCircle(
      imageData,
      source,
      fromX + (toX - fromX) * ratio,
      fromY + (toY - fromY) * ratio,
      restore,
    );
  }
};

export interface UseStickerCanvasResult {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  isReady: boolean;
  loadError: string | null;
  tool: StickerCanvasTool;
  setTool: (tool: StickerCanvasTool) => void;
  tolerance: number;
  setTolerance: (tolerance: number) => void;
  layers: StickerCanvasLayer[];
  selectedLayerId: string | null;
  selectLayer: (id: string | null) => void;
  setSelectedLayerScale: (scale: number) => void;
  addLayer: (kind: 'text' | 'emoji', value: string, options?: TextLayerOptions) => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
  canUndo: boolean;
  canRedo: boolean;
  isDirty: boolean;
  onPointerDown: (event: ReactPointerEvent<HTMLCanvasElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLCanvasElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLCanvasElement>) => void;
  exportPng: () => Promise<Blob>;
}

export function useStickerCanvas(sourceUrl: string | null): UseStickerCanvasResult {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sourceSnapshotRef = useRef<ImageData | null>(null);
  const historyRef = useRef<ReturnType<typeof createEditorHistory<EditorSnapshot>> | null>(null);
  const layersRef = useRef<StickerCanvasLayer[]>([]);
  const strokeRef = useRef<StrokeState | null>(null);
  const moveRef = useRef<MoveState | null>(null);
  const undoDepthRef = useRef(0);
  const redoDepthRef = useRef(0);
  const baselineAvailableRef = useRef(true);
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tool, setTool] = useState<StickerCanvasTool>('background');
  const [tolerance, setTolerance] = useState(24);
  const [layers, setLayers] = useState<StickerCanvasLayer[]>([]);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const renderSnapshot = useCallback((snapshot: EditorSnapshot) => {
    const context = canvasRef.current?.getContext('2d');
    if (!context) return;
    context.putImageData(snapshot.imageData, 0, 0);
    drawLayers(context, snapshot.layers);
  }, []);

  const syncLayers = useCallback((nextLayers: StickerCanvasLayer[]) => {
    layersRef.current = copyLayers(nextLayers);
    setLayers(copyLayers(nextLayers));
    setSelectedLayerId((current) => (
      current && nextLayers.some((layer) => layer.id === current) ? current : null
    ));
  }, []);

  const updateHistoryControls = useCallback(() => {
    setCanUndo(undoDepthRef.current > 0);
    setCanRedo(redoDepthRef.current > 0);
  }, []);

  const updateDirtyState = useCallback(() => {
    setIsDirty(!baselineAvailableRef.current || undoDepthRef.current > 0);
  }, []);

  const commit = useCallback((imageData: ImageData, nextLayers = layersRef.current) => {
    const history = historyRef.current;
    if (!history) return;
    const snapshot = { imageData: copyImageData(imageData), layers: copyLayers(nextLayers) };
    history.push(snapshot);
    if (undoDepthRef.current >= 29) baselineAvailableRef.current = false;
    undoDepthRef.current = Math.min(29, undoDepthRef.current + 1);
    redoDepthRef.current = 0;
    syncLayers(snapshot.layers);
    renderSnapshot(snapshot);
    updateDirtyState();
    updateHistoryControls();
  }, [renderSnapshot, syncLayers, updateDirtyState, updateHistoryControls]);

  const pointFromEvent = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: clampCoordinate((event.clientX - rect.left) * (canvas.width / rect.width)),
      y: clampCoordinate((event.clientY - rect.top) * (canvas.height / rect.height)),
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sourceUrl) return;

    let cancelled = false;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (cancelled) return;
      const context = canvas.getContext('2d');
      if (!context) {
        setLoadError('Браузер не поддерживает Canvas 2D.');
        return;
      }

      canvas.width = STICKER_CANVAS_SIZE;
      canvas.height = STICKER_CANVAS_SIZE;
      context.clearRect(0, 0, STICKER_CANVAS_SIZE, STICKER_CANVAS_SIZE);
      const scale = Math.min(STICKER_CANVAS_SIZE / image.naturalWidth, STICKER_CANVAS_SIZE / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      context.drawImage(image, (STICKER_CANVAS_SIZE - width) / 2, (STICKER_CANVAS_SIZE - height) / 2, width, height);
      let imageData: ImageData;
      try {
        imageData = context.getImageData(0, 0, STICKER_CANVAS_SIZE, STICKER_CANVAS_SIZE);
      } catch {
        setLoadError('Не удалось прочитать изображение для редактирования. Проверьте CORS-настройки.');
        setIsReady(false);
        return;
      }
      const initial = { imageData: copyImageData(imageData), layers: [] };
      sourceSnapshotRef.current = copyImageData(imageData);
      historyRef.current = createEditorHistory(initial);
      layersRef.current = [];
      undoDepthRef.current = 0;
      redoDepthRef.current = 0;
      baselineAvailableRef.current = true;
      setLayers([]);
      setSelectedLayerId(null);
      setCanUndo(false);
      setCanRedo(false);
      setIsDirty(false);
      setLoadError(null);
      setIsReady(true);
    };
    image.onerror = () => {
      if (!cancelled) {
        setLoadError('Не удалось загрузить изображение стикера.');
        setIsReady(false);
      }
    };
    image.src = sourceUrl;

    return () => { cancelled = true; };
  }, [sourceUrl]);

  const undo = useCallback(() => {
    const history = historyRef.current;
    if (!history || undoDepthRef.current === 0) return;
    const snapshot = history.undo();
    undoDepthRef.current -= 1;
    redoDepthRef.current += 1;
    syncLayers(snapshot.layers);
    renderSnapshot(snapshot);
    updateDirtyState();
    updateHistoryControls();
  }, [renderSnapshot, syncLayers, updateDirtyState, updateHistoryControls]);

  const redo = useCallback(() => {
    const history = historyRef.current;
    if (!history || redoDepthRef.current === 0) return;
    const snapshot = history.redo();
    redoDepthRef.current -= 1;
    undoDepthRef.current += 1;
    syncLayers(snapshot.layers);
    renderSnapshot(snapshot);
    updateDirtyState();
    updateHistoryControls();
  }, [renderSnapshot, syncLayers, updateDirtyState, updateHistoryControls]);

  const reset = useCallback(() => {
    const source = sourceSnapshotRef.current;
    if (!source) return;
    const initial = { imageData: copyImageData(source), layers: [] };
    historyRef.current = createEditorHistory(initial);
    syncLayers(initial.layers);
    renderSnapshot(initial);
    undoDepthRef.current = 0;
    redoDepthRef.current = 0;
    baselineAvailableRef.current = true;
    setSelectedLayerId(null);
    setIsDirty(false);
    updateHistoryControls();
  }, [renderSnapshot, syncLayers, updateHistoryControls]);

  const addLayer = useCallback((kind: 'text' | 'emoji', value: string, options: TextLayerOptions = {}) => {
    const current = historyRef.current?.current();
    if (!current || !value.trim()) return;
    const layer = {
      id: `${kind}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      kind,
      value: value.trim(),
      x: STICKER_CANVAS_SIZE / 2 - 40,
      y: STICKER_CANVAS_SIZE / 2,
      scale: 1,
      color: options.color ?? '#ffffff',
      fontSize: options.fontSize ?? 36,
    } satisfies StickerCanvasLayer;
    const nextLayers = [...layersRef.current, layer];
    commit(current.imageData, nextLayers);
    setSelectedLayerId(layer.id);
  }, [commit]);

  const selectLayer = useCallback((id: string | null) => setSelectedLayerId(id), []);

  const setSelectedLayerScale = useCallback((scale: number) => {
    const current = historyRef.current?.current();
    if (!current || !selectedLayerId) return;
    const selectedLayer = layersRef.current.find((layer) => layer.id === selectedLayerId);
    if (!selectedLayer) return;
    const boundedScale = Math.min(3, Math.max(0.25, scale));
    if (selectedLayer.scale === boundedScale) return;
    const nextLayers = layersRef.current.map((layer) => (
      layer.id === selectedLayerId ? { ...layer, scale: boundedScale } : layer
    ));
    commit(current.imageData, nextLayers);
  }, [commit, selectedLayerId]);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    const point = pointFromEvent(event);
    const current = historyRef.current?.current();
    if (!point || !current) return;
    if (typeof event.currentTarget.setPointerCapture === 'function') event.currentTarget.setPointerCapture(event.pointerId);

    if (tool === 'background') {
      const context = canvasRef.current?.getContext('2d');
      if (!context) return;
      let sample: Uint8ClampedArray;
      try {
        sample = context.getImageData(Math.round(point.x), Math.round(point.y), 1, 1).data;
      } catch {
        setLoadError('Не удалось прочитать изображение для редактирования. Проверьте CORS-настройки.');
        setIsReady(false);
        return;
      }
      commit(removeColor(current.imageData, [sample[0], sample[1], sample[2]] as RGB, tolerance), current.layers);
      return;
    }

    if (tool === 'erase' || tool === 'restore') {
      const source = sourceSnapshotRef.current;
      if (!source) return;
      const imageData = copyImageData(current.imageData);
      paintStroke(imageData, source, point.x, point.y, point.x, point.y, tool === 'restore');
      strokeRef.current = { imageData, lastX: point.x, lastY: point.y };
      renderSnapshot({ imageData, layers: current.layers });
      return;
    }

    if (tool === 'move' && selectedLayerId) {
      const layer = layersRef.current.find((candidate) => candidate.id === selectedLayerId);
      if (layer) moveRef.current = { layerId: layer.id, offsetX: point.x - layer.x, offsetY: point.y - layer.y };
    }
  }, [commit, pointFromEvent, renderSnapshot, tolerance, tool]);

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    const point = pointFromEvent(event);
    if (!point) return;
    const stroke = strokeRef.current;
    if (stroke && (tool === 'erase' || tool === 'restore')) {
      const source = sourceSnapshotRef.current;
      if (!source) return;
      paintStroke(stroke.imageData, source, stroke.lastX, stroke.lastY, point.x, point.y, tool === 'restore');
      stroke.lastX = point.x;
      stroke.lastY = point.y;
      renderSnapshot({ imageData: stroke.imageData, layers: layersRef.current });
      return;
    }

    const moving = moveRef.current;
    const current = historyRef.current?.current();
    if (!moving || !current) return;
    const nextLayers = layersRef.current.map((layer) => (
      layer.id === moving.layerId
        ? { ...layer, x: point.x - moving.offsetX, y: point.y - moving.offsetY }
        : layer
    ));
    syncLayers(nextLayers);
    renderSnapshot({ imageData: current.imageData, layers: nextLayers });
  }, [pointFromEvent, renderSnapshot, syncLayers, tool]);

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (typeof event.currentTarget.releasePointerCapture === 'function' && event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const stroke = strokeRef.current;
    if (stroke) {
      strokeRef.current = null;
      commit(stroke.imageData, layersRef.current);
      return;
    }
    if (moveRef.current) {
      moveRef.current = null;
      const current = historyRef.current?.current();
      if (current) commit(current.imageData, layersRef.current);
    }
  }, [commit]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      const isEditable = target instanceof HTMLElement && (
        target.isContentEditable || target.contentEditable === 'true' || target.getAttribute('contenteditable') === 'true'
        || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
      );
      if (event.isComposing || isEditable) return;
      if (!event.ctrlKey && !event.metaKey) return;
      if (event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo(); else undo();
      }
      if (event.key.toLowerCase() === 'y') {
        event.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [redo, undo]);

  const exportPng = useCallback(() => new Promise<Blob>((resolve, reject) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      reject(new Error('Холст редактора недоступен.'));
      return;
    }
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Не удалось подготовить PNG.'));
    }, 'image/png');
  }), []);

  return {
    canvasRef,
    isReady,
    loadError,
    tool,
    setTool,
    tolerance,
    setTolerance,
    layers,
    selectedLayerId,
    selectLayer,
    setSelectedLayerScale,
    addLayer,
    undo,
    redo,
    reset,
    canUndo,
    canRedo,
    isDirty,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    exportPng,
  };
}
